/**
 * Tables de référence : ce que l'agent IA consulte au lieu de deviner.
 *
 * Liste des tables → éditeur d'une table (colonnes, lignes, réponse, agents,
 * essai). Une seule règle pour tout le métier du client : des critères que
 * l'appelant fournit, des réponses que l'agent lit mot pour mot.
 */
import { useConfirmation } from '@/components/ui/useConfirmation'
import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ZoneVariables, versEcran, versServeur } from '@/components/manager/ZoneVariables'
import { Activity, AlertCircle, ArrowLeft, Bot, Check, ChevronDown, ChevronUp, Download, FlaskConical, History, LifeBuoy, Loader2, MapPin, MessageSquare, Plus, Search, Redo2, Table2, Trash2, Undo2, Upload, Users, X } from 'lucide-react'
import { toast } from 'sonner'
import { useProfile } from '@/api/profile'
import { useAIAgents } from '@/api/aiAgents'
import {
  useAddLookupRows, useDeleteLookupRow, useDeleteLookupTable, useLookupQueries, useLookupTable,
  useLookupTables, useSaveLookupTable, useTestLookup, useUpdateLookupRow,
  type LookupCalcul, type LookupField, type LookupFieldType, type LookupRow, type LookupRowValues, type LookupTable, type LookupTableInput,
} from '@/api/lookupTables'
import { GoogleAddressAutocomplete } from '@/components/GoogleAddressAutocomplete'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { formatDateHeure } from '@/lib/heure'


const inputCls = 'w-full bg-transparent border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-slate-400'
const labelCls = 'block text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5'
const cardCls = 'rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'

function slugify(s: string) {
  return s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40)
}

function videField(role: LookupField['role']): LookupField {
  return { name: '', role, type: role === 'answer' ? 'amount' : 'text', required: role === 'criterion', hint: '', options: [] }
}

// Colonnes que le serveur remplit depuis l'adresse du client, selon leur type.
const CALCULS: Record<LookupCalcul, LookupFieldType[]> = { distance: ['range', 'number'], ville: ['text', 'choice'] }

/** Sans colonne adresse, plus rien n'est calculé : les colonnes redeviennent des questions. */
function coherents(fields: LookupField[]): LookupField[] {
  if (fields.some((c) => c.type === 'adresse')) return fields
  return fields.map((c) => (c.calcul ? { ...c, calcul: undefined, required: true } : c))
}

/** Modèle prêt à remplir : le prix du déplacement selon la distance depuis l'atelier. */
function modeleDeplacement(t: (k: string, d: string) => string): Partial<LookupTableInput> {
  return {
    usage_hint: t('manager.lookup.tplTrip.usage', 'dès qu’on demande le prix du déplacement ou si on se déplace à une adresse'),
    fields: [
      { id: 'adresse', name: t('manager.lookup.tplTrip.address', 'Adresse du client'), role: 'criterion', type: 'adresse', required: true, hint: '', options: [], depart: '' },
      { id: 'distance', name: t('manager.lookup.tplTrip.distance', 'Distance (km)'), role: 'criterion', type: 'range', required: false, hint: '', options: [], calcul: 'distance' },
      { id: 'prix', name: t('manager.lookup.tplTrip.price', 'Prix du déplacement'), role: 'answer', type: 'amount', required: false, hint: '', options: [] },
    ],
    answer_template: t('manager.lookup.tplTrip.answer', 'À {distance} km, environ {duree} de route : le déplacement est de {prix}.'),
    fallback_text: t('manager.lookup.tplTrip.fallback', 'Cette adresse est hors de notre zone habituelle. Je prends vos coordonnées et quelqu’un vous rappelle avec un prix.'),
  }
}

// ─── Annulation ──────────────────────────────────────────────────────────────
// Le panneau des tables est modal : un toast en bas de page ne reçoit pas les
// clics. La barre « … supprimée · Annuler » vit donc dans la page elle-même.
type Annulation = { message: string; annuler: () => void }
const abonnes = new Set<(a: Annulation | null) => void>()
function annoncerAnnulation(message: string, annuler: () => void) {
  abonnes.forEach((f) => f({ message, annuler }))
}
function BarreAnnulation() {
  const { t } = useTranslation()
  const [a, setA] = useState<Annulation | null>(null)
  useEffect(() => {
    const f = (x: Annulation | null) => setA(x)
    abonnes.add(f)
    return () => { abonnes.delete(f) }
  }, [])
  useEffect(() => {
    if (!a) return
    const timer = setTimeout(() => setA(null), 7000)
    return () => clearTimeout(timer)
  }, [a])
  if (!a) return null
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900 px-4 py-2.5 text-sm">
      <span>{a.message}</span>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => { a.annuler(); setA(null) }} className="font-semibold underline underline-offset-2">{t('manager.lookup.undo', 'Annuler')}</button>
        <button type="button" onClick={() => setA(null)} className="opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
      </div>
    </div>
  )
}

// ─── Liste ───────────────────────────────────────────────────────────────────

export function LookupTablesView({ retour, retourLabel }: { retour?: () => void; retourLabel?: string } = {}) {
  const { data: profile } = useProfile()
  const companyId = profile?.company
  const { data: tables, isLoading } = useLookupTables(companyId)
  const [ouverte, setOuverte] = useState<number | 'new' | null>(null)

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6">
          {ouverte !== null ? (
            <LookupTableEditor companyId={companyId} tableId={ouverte === 'new' ? null : ouverte} onBack={() => setOuverte(null)} onSaved={(id) => setOuverte(id)} />
          ) : (
            <Liste tables={tables} isLoading={isLoading} ouvrir={setOuverte} retour={retour} retourLabel={retourLabel} />
          )}
        </div>
      </div>
    </div>
  )
}

function Liste({ tables, isLoading, ouvrir, retour, retourLabel }: { tables: LookupTable[] | undefined; isLoading: boolean; ouvrir: (id: number | 'new') => void; retour?: () => void; retourLabel?: string }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-0">
      <div className="flex flex-wrap items-start justify-between gap-4 pb-6">
        <div className="min-w-0">
          {retour && (
            <button type="button" onClick={retour} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 dark:hover:text-white mb-2">
              <ArrowLeft className="h-4 w-4" />{retourLabel || t('manager.lookup.backToAgents', 'Agents IA')}
            </button>
          )}
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{t('manager.lookup.title', 'Tables de référence')}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
            {t('manager.lookup.subtitle', 'Vos prix, tarifs, disponibilités : l’agent IA les consulte ici au lieu de les deviner, et lit la réponse mot pour mot.')}
          </p>
        </div>
        <Button onClick={() => ouvrir('new')} className="bg-blue-500 hover:bg-blue-600 text-white">
          <Plus className="h-4 w-4 mr-2" />{t('manager.lookup.new', 'Nouvelle table')}
        </Button>
      </div>

      {isLoading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : !tables?.length ? (
        <div className={cn(cardCls, 'p-12 text-center')}>
          <Table2 className="h-8 w-8 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="font-medium text-slate-900 dark:text-white">{t('manager.lookup.emptyTitle', 'Aucune table pour l’instant')}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
            {t('manager.lookup.emptyText', 'Créez vos colonnes (ce que l’agent demande, ce qu’il répond), remplissez vos lignes, et activez la table sur un agent.')}
          </p>
        </div>
      ) : (
        <div className={cn(cardCls, 'overflow-hidden')}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800 text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-medium">{t('manager.lookup.colTable', 'Table')}</th>
                <th className="px-5 py-3 font-medium">{t('manager.lookup.colCriteria', 'Critères')}</th>
                <th className="px-5 py-3 font-medium">{t('manager.lookup.colRows', 'Lignes')}</th>
                <th className="px-5 py-3 font-medium">{t('manager.lookup.colAgents', 'Agents')}</th>
                <th className="px-5 py-3 font-medium">{t('manager.lookup.colNone', 'Sans réponse')}</th>
              </tr>
            </thead>
            <tbody>
              {tables.map((tb) => (
                <tr key={tb.id} onClick={() => ouvrir(tb.id)} className="border-b last:border-0 border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60 cursor-pointer">
                  <td className="px-5 py-4">
                    <p className="font-medium text-slate-900 dark:text-white">{tb.name}</p>
                    <p className="text-xs text-slate-500 truncate max-w-md">{tb.usage_hint}</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">{tb.fields.filter((f) => f.role === 'criterion').map((f) => f.name).join(' · ')}</td>
                  <td className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">{tb.rows_count}</td>
                  <td className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">{tb.agent_ids.length}</td>
                  <td className="px-5 py-4">
                    {tb.none_count > 0 ? (
                      <span className="inline-flex items-center rounded-full bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">{tb.none_count}</span>
                    ) : <span className="text-sm text-slate-400">0</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Éditeur ─────────────────────────────────────────────────────────────────

function LookupTableEditor({ companyId, tableId, onBack, onSaved }: { companyId: number | undefined; tableId: number | null; onBack: () => void; onSaved: (id: number) => void }) {
  const { demander, dialogue } = useConfirmation()
  const { t } = useTranslation()
  const { data: table, isLoading } = useLookupTable(companyId, tableId)
  const { data: agents } = useAIAgents(companyId)
  const save = useSaveLookupTable(companyId)
  const supprimer = useDeleteLookupTable(companyId)

  const [form, setForm] = useState<LookupTableInput>({
    name: '', usage_hint: '', fields: [videField('criterion'), videField('answer')],
    answer_template: '', sms_enabled: true, sms_template: '', fallback_text: '', agent_ids: [],
  })
  const [onglet, setOnglet] = useState<'colonnes' | 'lignes' | 'reponse' | 'journal'>(tableId ? 'lignes' : 'colonnes')
  const [prefill, setPrefill] = useState<Record<string, string> | null>(null) // ligne manquante venue du journal

  // Chargement initial seulement : les rafraîchissements après auto-enregistrement
  // ne doivent pas écraser ce que le client est en train de taper.
  const chargee = useRef<number | null>(null)
  const dernierEnregistre = useRef<string>('')
  useEffect(() => {
    if (table && chargee.current !== table.id) {
      chargee.current = table.id
      const f = {
        name: table.name, usage_hint: table.usage_hint, fields: table.fields, answer_template: table.answer_template,
        sms_enabled: table.sms_enabled, sms_template: table.sms_template, fallback_text: table.fallback_text, agent_ids: table.agent_ids,
      }
      dernierEnregistre.current = JSON.stringify(f)
      setForm(f)
    }
  }, [table])

  // Historique pour les flèches ↶ ↷ : une entrée par modification, les frappes
  // rapprochées (< 600 ms) sont regroupées pour que Ctrl+Z ne remonte pas lettre par lettre.
  const historique = useRef<{ pile: LookupTableInput[]; index: number; dernier: number }>({ pile: [], index: -1, dernier: 0 })
  const formRef = useRef(form)
  formRef.current = form
  const patch = (p: Partial<LookupTableInput>) => {
    const f = formRef.current
    const h = historique.current
    const maintenant = Date.now()
    if (h.index < 0) { h.pile = [f]; h.index = 0 }
    else h.pile = h.pile.slice(0, h.index + 1)
    const suivant = { ...f, ...p }
    if (maintenant - h.dernier < 600 && h.index > 0) h.pile[h.index] = suivant
    else { h.pile.push(suivant); h.index = h.pile.length - 1 }
    h.dernier = maintenant
    formRef.current = suivant
    setForm(suivant)
  }
  const defaire = () => { const h = historique.current; if (h.index > 0) { h.index -= 1; h.dernier = 0; setForm(h.pile[h.index]) } }
  const refaire = () => { const h = historique.current; if (h.index < h.pile.length - 1) { h.index += 1; h.dernier = 0; setForm(h.pile[h.index]) } }
  const peutDefaire = historique.current.index > 0
  const peutRefaire = historique.current.index >= 0 && historique.current.index < historique.current.pile.length - 1
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const cible = e.target as HTMLElement | null
      if (cible && (cible.tagName === 'INPUT' || cible.tagName === 'TEXTAREA')) return // le champ gère son propre Ctrl+Z
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? refaire() : defaire() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [etat, setEtat] = useState<'ok' | 'pending' | 'error' | 'incomplete'>('ok')
  // Ce qui manque avant de pouvoir enregistrer : on attend, sans crier.
  const incomplet = (f: LookupTableInput) =>
    f.fields.some((c) => !c.name.trim() || (c.type === 'choice' && !c.options.length && c.calcul !== 'ville') || (c.type === 'adresse' && !c.depart?.trim())) || !f.fields.some((c) => c.role === 'criterion') || !f.fields.some((c) => c.role === 'answer')
  const enregistrer = async (donnees: LookupTableInput) => {
    if (!donnees.name.trim()) return
    try {
      const saved = await save.mutateAsync({ id: tableId ?? undefined, data: donnees })
      dernierEnregistre.current = JSON.stringify(donnees)
      setEtat('ok')
      if (!tableId) { chargee.current = saved.id; onSaved(saved.id) }
    } catch (e: unknown) {
      setEtat('error')
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
      toast.error(msg || t('manager.lookup.errSave', 'Impossible d’enregistrer'))
    }
  }

  // Auto-enregistrement : 1,2 s après la dernière modification d'une table existante.
  useEffect(() => {
    if (!tableId) return
    if (JSON.stringify(form) === dernierEnregistre.current) { setEtat((e) => (e === 'pending' || e === 'incomplete' ? 'ok' : e)); return }
    if (incomplet(form)) { setEtat('incomplete'); return }
    setEtat('pending')
    const timer = setTimeout(() => enregistrer(formRef.current), 1200)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, tableId])

  // En quittant, on n'attend pas le délai.
  const quitter = () => {
    if (tableId && JSON.stringify(formRef.current) !== dernierEnregistre.current && !incomplet(formRef.current)) void enregistrer(formRef.current)
    onBack()
  }

  if (tableId && isLoading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>

  const onglets: { id: typeof onglet; label: string; disabled?: boolean }[] = [
    { id: 'colonnes', label: t('manager.lookup.tabFields', 'Colonnes') },
    { id: 'lignes', label: t('manager.lookup.tabRows', 'Lignes'), disabled: !tableId },
    { id: 'reponse', label: t('manager.lookup.tabAnswer', 'Réponse et agents') },
    { id: 'journal', label: t('manager.lookup.tabLog', 'Consultations'), disabled: !tableId },
  ]

  return (
    <div className="space-y-6">
      {dialogue}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={quitter} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><ArrowLeft className="h-4 w-4" /></button>
          <input value={form.name} onChange={(e) => patch({ name: e.target.value })} placeholder={t('manager.lookup.namePh', 'Nom de la table, ex. Tarifs, Délais, Disponibilités')}
            className="text-2xl font-semibold bg-transparent text-slate-900 dark:text-white placeholder-slate-300 focus:outline-none min-w-0 w-full max-w-xl" />
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={defaire} disabled={!peutDefaire} title={t('manager.lookup.undo', 'Annuler')} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30"><Undo2 className="h-4 w-4" /></button>
          <button type="button" onClick={refaire} disabled={!peutRefaire} title={t('manager.lookup.redo', 'Rétablir')} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30"><Redo2 className="h-4 w-4" /></button>
          {tableId ? (
            <>
              <span className={cn('text-xs', etat === 'error' ? 'text-red-600' : etat === 'incomplete' ? 'text-amber-600' : 'text-slate-400')}>
                {etat === 'pending' ? t('manager.lookup.saving', 'Enregistrement…') : etat === 'error' ? t('manager.lookup.errSave', 'Impossible d’enregistrer') : etat === 'incomplete' ? t('manager.lookup.incomplete', 'À compléter') : t('manager.lookup.savedAuto', 'Enregistré')}
              </span>
              <Button variant="ghost" className="text-slate-500 hover:text-red-600" onClick={async () => {
                if (!(await demander({ titre: t('manager.lookup.confirmDelete', 'Supprimer cette table et toutes ses lignes ?') }))) return
                await supprimer.mutateAsync(tableId); onBack()
              }}><Trash2 className="h-4 w-4" /></Button>
            </>
          ) : (
            <Button onClick={() => form.name.trim() ? enregistrer(form) : toast.error(t('manager.lookup.errName', 'Donnez un nom à la table.'))} disabled={save.isPending} className="bg-blue-500 hover:bg-blue-600 text-white">
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('manager.lookup.create', 'Créer la table')}
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {onglets.map((o) => (
          <button key={o.id} disabled={o.disabled} onClick={() => setOnglet(o.id)}
            className={cn('px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors disabled:opacity-40',
              onglet === o.id ? 'border-blue-500 text-slate-900 dark:text-white' : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white')}>
            {o.label}
          </button>
        ))}
      </div>

      <BarreAnnulation />
      {onglet === 'colonnes' && <ChampsEditor form={form} patch={patch} nouvelle={!tableId} />}
      {onglet === 'lignes' && tableId && table && <LignesEditor companyId={companyId} table={table} prefill={prefill} />}
      {onglet === 'reponse' && <ReponseEditor form={form} patch={patch} agents={agents || []} companyId={companyId} tableId={tableId} />}
      {onglet === 'journal' && tableId && <JournalView companyId={companyId} tableId={tableId} table={table} onAjouter={(c) => { setPrefill(c); setOnglet('lignes') }} />}
    </div>
  )
}

// ─── Colonnes ────────────────────────────────────────────────────────────────

function ChampsEditor({ form, patch, nouvelle }: { form: LookupTableInput; patch: (p: Partial<LookupTableInput>) => void; nouvelle: boolean }) {
  const { t } = useTranslation()
  const [ouvert, setOuvert] = useState<number | null>(null)
  const setField = (i: number, p: Partial<LookupField>) => patch({ fields: coherents(form.fields.map((f, k) => (k === i ? { ...f, ...p } : f))) })
  const remove = (i: number) => {
    const champ = form.fields[i]
    const avant = form.fields
    patch({ fields: coherents(avant.filter((_, k) => k !== i)) }); setOuvert(null)
    // Geste destructeur : « Annuler » quelques secondes, comme Gmail ; l'auto-enregistrement suit.
    annoncerAnnulation(t('manager.lookup.fieldRemoved', 'Colonne « {{name}} » supprimée', { name: champ.name || '…' }), () => patch({ fields: avant }))
  }
  const ajouter = (role: LookupField['role']) => { patch({ fields: [...form.fields, videField(role)] }); setOuvert(form.fields.length) }
  const deplacer = (i: number, sens: -1 | 1) => {
    const j = i + sens
    if (j < 0 || j >= form.fields.length || form.fields[j].role !== form.fields[i].role) return
    const fields = [...form.fields]; [fields[i], fields[j]] = [fields[j], fields[i]]
    patch({ fields }); setOuvert(ouvert === i ? j : ouvert)
  }
  const criteres = form.fields.map((f, i) => ({ f, i })).filter((x) => x.f.role === 'criterion')
  const reponses = form.fields.map((f, i) => ({ f, i })).filter((x) => x.f.role === 'answer')
  const aAdresse = form.fields.some((f) => f.type === 'adresse')

  // Une colonne devient l'adresse du client : sans colonne calculée, on ajoute la
  // distance juste après, sinon l'adresse ne servirait à rien.
  const changerType = (i: number, type: LookupFieldType) => {
    const f = form.fields[i]
    if (type === 'adresse') {
      const fields: LookupField[] = form.fields.map((c, k) => (k === i ? { ...c, type, required: true, calcul: undefined } : c))
      if (!fields.some((c) => c.calcul)) {
        fields.splice(i + 1, 0, { name: t('manager.lookup.tplTrip.distance', 'Distance (km)'), role: 'criterion', type: 'range', required: false, hint: '', options: [], calcul: 'distance' })
      }
      patch({ fields }); setOuvert(i)
      return
    }
    setField(i, { type, ...(f.calcul && !CALCULS[f.calcul].includes(type) ? { calcul: undefined, required: true } : {}) })
    if (type === 'choice' && !f.options.length && !f.calcul) setOuvert(i)
  }

  // Côté question, le type dit comment comparer ; côté réponse, il ne dit que
  // comment lire (montant en dollars, oui/non, nombre tel quel).
  const types: { id: LookupFieldType; label: string; ex: string; pour: 'both' | 'criterion' | 'answer' }[] = [
    { id: 'text', label: t('manager.lookup.type.text', 'Réponse libre'), ex: t('manager.lookup.typeEx.text', 'ex. le nom d’un produit, d’un quartier'), pour: 'criterion' },
    { id: 'choice', label: t('manager.lookup.type.choice', 'Un choix dans une liste'), ex: t('manager.lookup.typeEx.choice', 'ex. Résidentiel, Commercial'), pour: 'criterion' },
    { id: 'range', label: t('manager.lookup.type.range', 'Une année ou un nombre'), ex: t('manager.lookup.typeEx.range', 'la ligne couvre une plage, ex. 2016-2021 ou 1-5'), pour: 'criterion' },
    { id: 'number', label: t('manager.lookup.type.number', 'Un nombre exact'), ex: t('manager.lookup.typeEx.number', 'ex. 3 chambres'), pour: 'criterion' },
    { id: 'boolean', label: t('manager.lookup.type.boolean', 'Oui ou non'), ex: t('manager.lookup.typeEx.boolean', 'ex. urgent ?'), pour: 'criterion' },
    { id: 'adresse', label: t('manager.lookup.type.adresse', 'Adresse du client'), ex: t('manager.lookup.typeEx.adresse', 'l’agent demande l’adresse ; la distance, le temps de route et la ville sont calculés'), pour: 'criterion' },
    { id: 'text', label: t('manager.lookup.typeAns.text', 'Texte'), ex: t('manager.lookup.typeAnsEx.text', 'lu tel quel, ex. « Taxes en sus. »'), pour: 'answer' },
    { id: 'amount', label: t('manager.lookup.type.amount', 'Un montant en dollars'), ex: t('manager.lookup.typeEx.amount', 'lu « 130 dollars », écrit « 130 $ »'), pour: 'answer' },
    { id: 'number', label: t('manager.lookup.typeAns.number', 'Un nombre'), ex: t('manager.lookup.typeAnsEx.number', 'lu tel quel, ex. 3 jours, 2 places'), pour: 'answer' },
  ]

  // La phrase qui dit ce que l'agent fera : elle change pendant qu'on édite.
  const noms = (items: { f: LookupField }[]) => items.map((x) => x.f.name.trim()).filter(Boolean)
  const liste = (l: string[]) => l.length <= 1 ? l.join('') : `${l.slice(0, -1).join(', ')} ${t('manager.lookup.and', 'et')} ${l[l.length - 1]}`
  const nc = noms(criteres.filter((x) => !x.f.calcul)), ncalc = noms(criteres.filter((x) => x.f.calcul)), nr = noms(reponses)
  const apercu = !(nc.length && nr.length)
    ? t('manager.lookup.previewEmpty', 'Nommez au moins un critère et une réponse : la phrase de l’agent s’écrira ici.')
    : ncalc.length
      ? t('manager.lookup.previewComputed', 'L’agent demande {{criteres}}, calcule {{calculees}}, cherche la ligne qui correspond, puis lit {{reponses}}.', { criteres: liste(nc), calculees: liste(ncalc), reponses: liste(nr) })
      : t('manager.lookup.preview', 'L’agent demande {{criteres}}, cherche la ligne qui correspond, puis lit {{reponses}}.', { criteres: liste(nc), reponses: liste(nr) })

  const ligne = (f: LookupField, i: number, premier: boolean, dernier: boolean) => {
    const est = ouvert === i
    const role = f.role
    return (
      <div className={cn('rounded-lg border transition-colors', est ? 'border-blue-300 dark:border-blue-500/50 bg-blue-50/30 dark:bg-blue-500/5' : 'border-slate-200 dark:border-slate-800')}>
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="flex flex-col -my-1">
            <button onClick={() => deplacer(i, -1)} disabled={premier} className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-30" title={t('manager.lookup.moveUp', 'Monter')}><ChevronUp className="h-3.5 w-3.5" /></button>
            <button onClick={() => deplacer(i, 1)} disabled={dernier} className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-30" title={t('manager.lookup.moveDown', 'Descendre')}><ChevronDown className="h-3.5 w-3.5" /></button>
          </div>
          <input className={cn(inputCls, 'py-1.5 flex-1 min-w-0')} value={f.name} autoFocus={est && !f.name}
            onChange={(e) => setField(i, { name: e.target.value, id: f.id || undefined })}
            placeholder={role === 'criterion' ? t('manager.lookup.critPh', 'ex. Type de service') : t('manager.lookup.ansPh', 'ex. Tarif')} />
          <div className="relative w-56 shrink-0 hidden sm:block">
            <select className={cn(inputCls, 'py-1.5 appearance-none pr-8')} value={f.type} onChange={(e) => changerType(i, e.target.value as LookupFieldType)}>
              {types.filter((ty) => ty.pour === role && (ty.id !== 'adresse' || f.type === 'adresse' || !aAdresse)).map((ty) => <option key={ty.id} value={ty.id}>{ty.label}</option>)}
            </select>
            <ChevronDown className="h-4 w-4 absolute right-2.5 top-2 text-slate-400 pointer-events-none" />
          </div>
          {role === 'criterion' && (f.calcul ? (
            <span className="hidden md:inline-flex items-center gap-1 rounded-full bg-blue-50 dark:bg-blue-500/10 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 shrink-0" title={t('manager.lookup.computedHelp', 'Remplie depuis l’adresse du client : l’agent ne la demande pas.')}>
              <MapPin className="h-3 w-3" />{t('manager.lookup.computed', 'Calculée')}
            </span>
          ) : (
            <label className="hidden md:inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 shrink-0 cursor-pointer" title={t('manager.lookup.requiredHelp', 'L’agent le demande toujours avant de chercher.')}>
              <Switch checked={f.required} disabled={f.type === 'adresse'} onCheckedChange={(v) => setField(i, { required: v })} />{t('manager.lookup.required', 'Obligatoire')}
            </label>
          ))}
          <button onClick={() => setOuvert(est ? null : i)} className={cn('p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800', est && 'text-blue-600')} title={t('manager.lookup.details', 'Détails')}>
            <ChevronDown className={cn('h-4 w-4 transition-transform', est && 'rotate-180')} />
          </button>
          <button onClick={() => remove(i)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"><X className="h-4 w-4" /></button>
        </div>
        {est && (
          <div className="px-3 pb-3 pt-1 space-y-3 border-t border-slate-200/70 dark:border-slate-800">
            <p className="text-xs text-slate-500 pt-2">{
              f.calcul === 'distance' ? t('manager.lookup.computedEx.distance', 'Distance en voiture depuis l’adresse de départ. Dans les lignes, écrivez des tranches, ex. 0-10 puis 10-25 : 10 km pile prend la première.')
                : f.calcul === 'ville' ? t('manager.lookup.computedEx.ville', 'La ville de l’adresse du client. Dans les lignes, une ville par ligne, ex. Laval.')
                  : types.find((ty) => ty.id === f.type && ty.pour === role)?.ex
            }</p>
            {f.type === 'adresse' && (
              <div>
                <label className={labelCls}>{t('manager.lookup.depart', 'Adresse de départ')}</label>
                <GoogleAddressAutocomplete value={f.depart || ''} onChange={(depart) => setField(i, { depart })} className="h-9 text-sm"
                  placeholder={t('manager.lookup.departPh', 'L’adresse de votre atelier ou de votre bureau')} />
                <p className={cn('text-xs mt-1.5', f.depart?.trim() ? 'text-slate-500' : 'text-amber-700 dark:text-amber-300')}>
                  {t('manager.lookup.departHelp', 'Les distances et les temps de route partent d’ici. L’agent redit toujours l’adresse comprise.')}
                </p>
              </div>
            )}
            {role === 'criterion' && aAdresse && f.type !== 'adresse' && (CALCULS.distance.includes(f.type) || CALCULS.ville.includes(f.type)) && (
              <div>
                <label className={labelCls}>{t('manager.lookup.filledBy', 'Remplie par')}</label>
                <div className="relative">
                  <select className={cn(inputCls, 'appearance-none pr-8')} value={f.calcul || ''}
                    onChange={(e) => { const calcul = (e.target.value || undefined) as LookupCalcul | undefined; setField(i, { calcul, required: !calcul }) }}>
                    <option value="">{t('manager.lookup.filledByCaller', 'L’appelant : l’agent pose la question')}</option>
                    {CALCULS.distance.includes(f.type) && <option value="distance">{t('manager.lookup.filledByDistance', 'La distance depuis l’adresse, en km')}</option>}
                    {CALCULS.ville.includes(f.type) && <option value="ville">{t('manager.lookup.filledByCity', 'La ville de l’adresse')}</option>}
                  </select>
                  <ChevronDown className="h-4 w-4 absolute right-2.5 top-2.5 text-slate-400 pointer-events-none" />
                </div>
              </div>
            )}
            {role === 'criterion' && !f.calcul && (
              <div><label className={labelCls}>{t('manager.lookup.hint', 'Comment poser la question (facultatif)')}</label>
                <input className={inputCls} value={f.hint} onChange={(e) => setField(i, { hint: e.target.value })} placeholder={t('manager.lookup.hintPh', 'ex. Demandez si c’est urgent.')} /></div>
            )}
            {role === 'criterion' && f.type === 'choice' && <ChoixEditor options={f.options} onChange={(options) => setField(i, { options })} />}
          </div>
        )}
      </div>
    )
  }

  const bloc = (titre: string, texte: string, items: { f: LookupField; i: number }[], role: LookupField['role']) => (
    <div className={cn(cardCls, 'p-5')}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div><p className="font-medium text-slate-900 dark:text-white">{titre}</p><p className="text-sm text-slate-500 dark:text-slate-400">{texte}</p></div>
        <Button variant="outline" size="sm" onClick={() => ajouter(role)}><Plus className="h-3.5 w-3.5 mr-1.5" />{t('manager.lookup.addField', 'Ajouter')}</Button>
      </div>
      <div className="space-y-2">
        {items.map(({ f, i }, k) => <Fragment key={i}>{ligne(f, i, k === 0, k === items.length - 1)}</Fragment>)}
        {!items.length && <p className="text-sm text-slate-400 py-2">{t('manager.lookup.noField', 'Aucune colonne. Cliquez Ajouter.')}</p>}
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {nouvelle && form.fields.every((f) => !f.name.trim()) && (
        <div className={cn(cardCls, 'px-5 py-4 flex flex-wrap items-center justify-between gap-3')}>
          <div className="flex items-start gap-3 min-w-0">
            <MapPin className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-white">{t('manager.lookup.tplTrip.title', 'Prix du déplacement selon l’adresse')}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('manager.lookup.tplTrip.text', 'L’agent demande l’adresse, la distance est calculée, il lit le prix de la bonne tranche.')}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => { patch({ ...modeleDeplacement(t), name: form.name.trim() || t('manager.lookup.tplTrip.name', 'Déplacement') }); setOuvert(0) }}>
            {t('manager.lookup.tplTrip.use', 'Utiliser ce modèle')}
          </Button>
        </div>
      )}
      <div className="rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 px-5 py-4 flex items-start gap-3">
        <Bot className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
        <p className="text-sm text-slate-800 dark:text-slate-100">{apercu}</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className={cn(cardCls, 'p-5')}>
            <label className={labelCls}>{t('manager.lookup.usage', 'Quand l’agent doit consulter cette table')}</label>
            <input className={inputCls} value={form.usage_hint} onChange={(e) => patch({ usage_hint: e.target.value })} placeholder={t('manager.lookup.usagePh', 'ex. dès qu’on demande un prix ou un délai')} />
            <p className="text-xs text-slate-500 mt-2">{t('manager.lookup.usageHelp', 'C’est ce qui décide quand l’agent regarde ici.')}</p>
          </div>
          {bloc(t('manager.lookup.criteriaTitle', 'Ce que l’agent demande'), t('manager.lookup.criteriaText', 'Dans cet ordre. Chaque critère devient une question.'), criteres, 'criterion')}
        </div>
        {bloc(t('manager.lookup.answersTitle', 'Ce que l’agent lit'), t('manager.lookup.answersText', 'Tel quel, une fois la ligne trouvée.'), reponses, 'answer')}
      </div>
    </div>
  )
}

/** Les valeurs admises d'un critère, en étiquettes. Un clic sur une étiquette
 *  ouvre ses synonymes (les autres façons de le dire au téléphone). */
function ChoixEditor({ options, onChange }: { options: LookupField['options']; onChange: (o: LookupField['options']) => void }) {
  const { t } = useTranslation()
  const [saisie, setSaisie] = useState('')
  const [sel, setSel] = useState<number | null>(null)
  const ajouter = () => {
    const v = saisie.trim()
    if (!v) return
    if (options.some((o) => o.value.toLowerCase() === v.toLowerCase())) { setSaisie(''); return }
    onChange([...options, { value: v, synonyms: [] }]); setSaisie('')
  }
  return (
    <div>
      <label className={labelCls}>{t('manager.lookup.options', 'Valeurs possibles')}</label>
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-700 px-2 py-1.5 bg-white dark:bg-slate-900">
        {options.map((o, k) => (
          <button key={k} type="button" onClick={() => setSel(sel === k ? null : k)}
            className={cn('inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-sm', sel === k ? 'bg-blue-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-200')}>
            {o.value}{o.synonyms?.length ? <span className={cn('text-[11px]', sel === k ? 'text-blue-100' : 'text-slate-400')}>+{o.synonyms.length}</span> : null}
          </button>
        ))}
        <input className="flex-1 min-w-[140px] bg-transparent text-sm py-0.5 focus:outline-none placeholder-slate-400" value={saisie}
          onChange={(e) => setSaisie(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); ajouter() } }} onBlur={ajouter}
          placeholder={options.length ? t('manager.lookup.addChoice', 'Ajouter…') : t('manager.lookup.addChoiceFirst', 'Tapez une valeur puis Entrée, ex. Résidentiel')} />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={ajouter} disabled={!saisie.trim()} title={t('manager.lookup.addChoiceBtn', 'Ajouter cette valeur')}
          className="shrink-0 h-6 w-6 inline-flex items-center justify-center rounded-md bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-30 disabled:hover:bg-blue-500">
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
      {sel !== null && options[sel] && (
        <div className="mt-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2.5 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{options[sel].value}</span>
          <input className={cn(inputCls, 'py-1 flex-1 min-w-[200px]')} defaultValue={(options[sel].synonyms || []).join(', ')} key={sel}
            onBlur={(e) => onChange(options.map((o, k) => k === sel ? { ...o, synonyms: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) } : o))}
            placeholder={t('manager.lookup.synonymsPh', 'Autres façons de le dire, séparées par des virgules : maison, résidence')} />
          <button type="button" onClick={() => {
            const avant = options
            onChange(options.filter((_, k) => k !== sel)); setSel(null)
            annoncerAnnulation(t('manager.lookup.choiceRemoved', '« {{v}} » retiré', { v: options[sel].value }), () => onChange(avant))
          }} className="text-xs text-slate-500 hover:text-red-600">{t('manager.lookup.removeChoice', 'Retirer')}</button>
        </div>
      )}
    </div>
  )
}

// ─── Lignes ──────────────────────────────────────────────────────────────────

/** Colonne de type « choix » : on choisit dans la liste des valeurs de la colonne au lieu d'écrire.
 *  Une valeur d'une ancienne ligne qui n'est plus dans la liste reste visible pour ne pas la perdre. */
function ChoixSelect({ options, value, onChange, onKeyDown, className }: {
  options: LookupField['options']; value: string; onChange: (v: string) => void; onKeyDown?: (e: KeyboardEvent<HTMLSelectElement>) => void; className?: string
}) {
  const { t } = useTranslation()
  const orpheline = value && !options.some((o) => o.value === value)
  return (
    <select className={cn(className, 'text-sm text-slate-900 dark:text-slate-100 focus:outline-none appearance-none pr-6')} value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown}>
      <option value="">{t('manager.lookup.choosePh', 'Choisir…')}</option>
      {orpheline && <option value={value}>{value}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
    </select>
  )
}

function cellToText(v: LookupRowValues[string]): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'object') return `${v.min ?? ''}-${v.max ?? ''}`
  if (typeof v === 'boolean') return v ? 'oui' : 'non'
  return String(v)
}

function LignesEditor({ companyId, table, prefill }: { companyId: number | undefined; table: LookupTable; prefill?: Record<string, string> | null }) {
  const { demander, dialogue } = useConfirmation()
  const { t } = useTranslation()
  const add = useAddLookupRows(companyId, table.id)
  const update = useUpdateLookupRow(companyId, table.id)
  const del = useDeleteLookupRow(companyId, table.id)
  const [nouvelle, setNouvelle] = useState<Record<string, string>>(prefill || {})
  useEffect(() => {
    if (prefill) {
      setNouvelle(prefill)
      // Le curseur va sur la première réponse vide : il ne reste que le prix à taper.
      requestAnimationFrame(() => premiereReponse.current?.focus())
    }
  }, [prefill])
  const [edition, setEdition] = useState<{ id: number; values: Record<string, string>; champ?: string } | null>(null)
  const [recherche, setRecherche] = useState('')
  const [cochees, setCochees] = useState<Set<number>>(new Set())
  const premiereCellule = useRef<HTMLInputElement>(null)
  const premiereReponse = useRef<HTMLInputElement>(null)
  const celluleEditee = useRef<HTMLInputElement>(null)
  const fichier = useRef<HTMLInputElement>(null)
  const champs = table.fields.filter((f) => f.type !== 'adresse')
  const adresse = table.fields.find((f) => f.type === 'adresse')
  const lignes = table.rows || []

  const q = normaliserTexte(recherche)
  const visibles = q ? lignes.filter((r) => champs.some((f) => normaliserTexte(cellToText(r.values[f.id])).includes(q))) : lignes

  useEffect(() => { if (edition?.champ) requestAnimationFrame(() => celluleEditee.current?.select()) }, [edition?.id, edition?.champ])

  const envoyer = async () => {
    if (!Object.values(nouvelle).some((v) => v.trim())) return
    try {
      const r = await add.mutateAsync([{ values: nouvelle }])
      if (r.errors.length) toast.error(r.errors[0].error)
      else { setNouvelle({}); premiereCellule.current?.focus(); toast.success(t('manager.lookup.rowAdded', 'Ligne ajoutée')) }
    } catch (e: unknown) {
      // 400 = toutes les lignes refusées : le détail est dans errors[0]
      const data = (e as { response?: { data?: { errors?: { error: string }[]; error?: string } } })?.response?.data
      toast.error(data?.errors?.[0]?.error || data?.error || t('manager.lookup.errSave', 'Impossible d’enregistrer'))
    }
  }
  const sauver = async () => {
    if (!edition) return
    try { await update.mutateAsync({ rowId: edition.id, values: edition.values }); setEdition(null) }
    catch (e: unknown) { toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || t('manager.lookup.errSave', 'Impossible d’enregistrer')) }
  }
  const editer = (r: LookupRow, champ: string) => {
    if (edition?.id === r.id) return
    setEdition({ id: r.id, champ, values: Object.fromEntries(champs.map((f) => [f.id, cellToText(r.values[f.id])])) })
  }
  // Suppression d'une ligne : « Annuler » la recrée (mêmes valeurs, nouvel identifiant).
  const supprimerLigne = async (r: LookupRow) => {
    await del.mutateAsync(r.id)
    const valeurs = Object.fromEntries(champs.map((f) => [f.id, cellToText(r.values[f.id])]))
    annoncerAnnulation(t('manager.lookup.rowRemoved', 'Ligne supprimée'), () => { void add.mutateAsync([{ values: valeurs }]) })
  }
  const supprimerCochees = async () => {
    if (!(await demander({ titre: t('manager.lookup.confirmDeleteRows', 'Supprimer {{n}} ligne(s) ?', { n: cochees.size }) }))) return
    for (const id of cochees) await del.mutateAsync(id)
    setCochees(new Set())
  }
  const toutCocher = (on: boolean) => setCochees(on ? new Set(visibles.map((r) => r.id)) : new Set())

  // Export : une colonne par champ, en-têtes = noms des colonnes, séparateur point-virgule (Excel FR).
  const exporter = () => {
    const ech = (v: string) => /[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
    const csv = [champs.map((f) => ech(f.name)).join(';'), ...lignes.map((r) => champs.map((f) => ech(cellToText(r.values[f.id]))).join(';'))].join('\n')
    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }))
    a.download = `${table.slug || 'table'}.csv`; a.click(); URL.revokeObjectURL(a.href)
  }
  // Import : l'en-tête est rapproché des noms de colonnes (accents et casse ignorés) ; le serveur valide chaque ligne.
  const importer = async (f: File) => {
    const texte = await f.text()
    const { entetes, lignes: brut } = lireCsv(texte)
    const map = entetes.map((h) => champs.find((c) => normaliserTexte(c.name) === normaliserTexte(h) || c.id === normaliserTexte(h).replace(/[^a-z0-9]+/g, '_'))?.id || null)
    if (!map.some(Boolean)) return toast.error(t('manager.lookup.importNoMatch', 'Aucune colonne du fichier ne correspond aux colonnes de la table. Vérifiez la première ligne du fichier.'))
    const rows = brut.filter((l) => l.some((v) => v.trim())).map((l) => ({ values: Object.fromEntries(l.map((v, i) => [map[i], v.trim()]).filter(([k]) => k) as [string, string][]) }))
    if (!rows.length) return toast.error(t('manager.lookup.importEmpty', 'Le fichier ne contient aucune ligne.'))
    try {
      const r = await add.mutateAsync(rows)
      if (r.errors.length) toast.warning(t('manager.lookup.importPartial', '{{ok}} ligne(s) ajoutée(s), {{ko}} refusée(s). Première : ligne {{n}}, {{err}}', { ok: r.created.length, ko: r.errors.length, n: r.errors[0].ligne + 1, err: r.errors[0].error }))
      else toast.success(t('manager.lookup.importOk', '{{ok}} ligne(s) ajoutée(s)', { ok: r.created.length }))
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { errors?: { ligne: number; error: string }[] } } })?.response?.data
      toast.error(data?.errors?.[0] ? t('manager.lookup.importFail', 'Aucune ligne ajoutée. Ligne {{n}} : {{err}}', { n: data.errors[0].ligne + 1, err: data.errors[0].error }) : t('manager.lookup.errSave', 'Impossible d’enregistrer'))
    }
  }

  const placeholder = (f: LookupField) => f.calcul === 'distance' ? (f.type === 'range' ? '0-10' : '10') : f.calcul === 'ville' && f.type !== 'choice' ? 'Laval'
    : f.type === 'range' ? '2016-2021' : f.type === 'amount' ? '189' : f.type === 'boolean' ? 'oui / non' : f.type === 'choice' ? f.options[0]?.value || '' : ''
  const toutesCochees = visibles.length > 0 && visibles.every((r) => cochees.has(r.id))

  return (
    <div className={cn(cardCls, 'overflow-hidden')}>
      {dialogue}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
          <input className={cn(inputCls, 'pl-9 py-1.5')} value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder={t('manager.lookup.searchRows', 'Rechercher dans les lignes')} />
        </div>
        {cochees.size > 0 ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-600 dark:text-slate-300">{t('manager.lookup.selected', '{{n}} sélectionnée(s)', { n: cochees.size })}</span>
            <Button variant="outline" size="sm" onClick={supprimerCochees} className="text-red-600 hover:text-red-700"><Trash2 className="h-3.5 w-3.5 mr-1.5" />{t('manager.lookup.deleteRows', 'Supprimer')}</Button>
          </div>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <input ref={fichier} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void importer(f); e.target.value = '' }} />
          <Button variant="outline" size="sm" onClick={() => fichier.current?.click()} disabled={add.isPending}><Upload className="h-3.5 w-3.5 mr-1.5" />{t('manager.lookup.import', 'Importer')}</Button>
          <Button variant="outline" size="sm" onClick={exporter} disabled={!lignes.length}><Download className="h-3.5 w-3.5 mr-1.5" />{t('manager.lookup.export', 'Exporter')}</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-800 text-[11px] uppercase tracking-wider text-slate-500">
            <th className="w-10 px-4 py-3"><input type="checkbox" className="accent-blue-500" checked={toutesCochees} onChange={(e) => toutCocher(e.target.checked)} /></th>
            {champs.map((f) => (
              <th key={f.id} className={cn('px-4 py-3 font-medium whitespace-nowrap', f.role === 'answer' && 'text-blue-600 dark:text-blue-400')}>{f.name}{f.required && f.role === 'criterion' && ' *'}</th>
            ))}
            <th className="w-20" />
          </tr>
        </thead>
        <tbody>
          {visibles.map((r: LookupRow) => {
            const enEdition = edition?.id === r.id
            return (
              <tr key={r.id} className={cn('border-b border-slate-100 dark:border-slate-800 group', (cochees.has(r.id) || enEdition) && 'bg-blue-50/50 dark:bg-blue-500/5')}>
                <td className="px-4 py-2.5"><input type="checkbox" className="accent-blue-500" checked={cochees.has(r.id)} onChange={(e) => { const n = new Set(cochees); e.target.checked ? n.add(r.id) : n.delete(r.id); setCochees(n) }} /></td>
                {champs.map((f) => (
                  <td key={f.id} className="px-4 py-2.5 text-slate-800 dark:text-slate-200 whitespace-nowrap cursor-text" onClick={() => editer(r, f.id)}>
                    {enEdition ? (
                      f.type === 'choice' ? (
                        <ChoixSelect options={f.options} value={edition!.values[f.id] ?? ''} className="w-full min-w-[180px] bg-transparent border-0 border-b border-blue-400 rounded-none px-0 py-0.5"
                          onChange={(v) => setEdition({ ...edition!, values: { ...edition!.values, [f.id]: v } })}
                          onKeyDown={(e) => { if (e.key === 'Enter') sauver(); if (e.key === 'Escape') setEdition(null) }} />
                      ) : (
                      <input ref={edition!.champ === f.id ? celluleEditee : undefined}
                        className="w-full bg-transparent border-0 border-b border-blue-400 px-0 py-0.5 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-0"
                        value={edition!.values[f.id] ?? ''}
                        onChange={(e) => setEdition({ ...edition!, values: { ...edition!.values, [f.id]: e.target.value } })}
                        onKeyDown={(e) => { if (e.key === 'Enter') sauver(); if (e.key === 'Escape') setEdition(null) }} />
                      )
                    ) : (cellToText(r.values[f.id]) || <span className="text-slate-300">—</span>)}
                  </td>
                ))}
                <td className="px-2 py-2.5 text-right whitespace-nowrap">
                  {enEdition ? (
                    <>
                      <button onClick={sauver} className="p-1.5 rounded text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10" title={t('manager.lookup.save', 'Enregistrer')}><Check className="h-4 w-4" /></button>
                      <button onClick={() => setEdition(null)} className="p-1.5 rounded text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-4 w-4" /></button>
                    </>
                  ) : (
                    <button onClick={() => supprimerLigne(r)} className="p-1.5 rounded text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100"><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            )
          })}
          {!visibles.length && q && (
            <tr><td colSpan={champs.length + 2} className="px-4 py-8 text-center text-sm text-slate-400">{t('manager.lookup.noMatch', 'Aucune ligne ne contient « {{q}} ».', { q: recherche })}</td></tr>
          )}
          <tr className="bg-slate-50/60 dark:bg-slate-800/40">
            <td className="px-4 py-2.5 text-slate-300"><Plus className="h-4 w-4" /></td>
            {champs.map((f, i) => (
              <td key={f.id} className="px-4 py-2.5">
                {f.type === 'choice' ? (
                  <ChoixSelect options={f.options} value={nouvelle[f.id] ?? ''} className={cn(inputCls, 'py-1.5 bg-white dark:bg-slate-900')}
                    onChange={(v) => setNouvelle({ ...nouvelle, [f.id]: v })} onKeyDown={(e) => e.key === 'Enter' && envoyer()} />
                ) : (
                <input ref={i === 0 ? premiereCellule : f.role === 'answer' && champs.findIndex((c) => c.role === 'answer') === i ? premiereReponse : undefined} className={cn(inputCls, 'py-1.5 bg-white dark:bg-slate-900')} value={nouvelle[f.id] ?? ''} placeholder={placeholder(f)}
                  onChange={(e) => setNouvelle({ ...nouvelle, [f.id]: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && envoyer()} />
                )}
              </td>
            ))}
            <td className="px-2 py-2.5 text-right">
              <Button size="sm" onClick={envoyer} disabled={add.isPending} className="bg-blue-500 hover:bg-blue-600 text-white"><Plus className="h-3.5 w-3.5" /></Button>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 dark:border-slate-800">
        <span className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs font-medium text-slate-600 dark:text-slate-300">
          {q && visibles.length !== lignes.length ? t('manager.lookup.countFiltered', '{{n}} sur {{total}} lignes', { n: visibles.length, total: lignes.length }) : t('manager.lookup.count', '{{n}} ligne(s)', { n: lignes.length })}
        </span>
        <span className="text-xs text-slate-400">{adresse
          ? t('manager.lookup.rowsHelpTrip', 'Distances en km depuis {{depart}}. Une tranche s’écrit 0-10.', { depart: adresse.depart || '…' })
          : t('manager.lookup.rowsHelp', 'Entrée pour ajouter. Clic sur une case pour la modifier. Une plage s’écrit 2016-2021.')}</span>
      </div>
    </div>
  )
}

function normaliserTexte(s: string) {
  return s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
}

/** Lecture d'un CSV simple : séparateur détecté (; , tabulation), guillemets gérés. */
function lireCsv(texte: string): { entetes: string[]; lignes: string[][] } {
  const src = texte.replace(/^\ufeff/, '')
  const premiere = src.split(/\r?\n/)[0] || ''
  const sep = [';', ',', '\t'].map((c) => [c, (premiere.match(new RegExp(c === '\t' ? '\t' : `\\${c}`, 'g')) || []).length] as const).sort((a, b) => b[1] - a[1])[0][0]
  const lignes: string[][] = []
  let ligne: string[] = [], cell = '', guill = false
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]
    if (guill) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++ }
      else if (ch === '"') guill = false
      else cell += ch
    } else if (ch === '"') guill = true
    else if (ch === sep) { ligne.push(cell); cell = '' }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && src[i + 1] === '\n') i++; ligne.push(cell); lignes.push(ligne); ligne = []; cell = '' }
    else cell += ch
  }
  if (cell || ligne.length) { ligne.push(cell); lignes.push(ligne) }
  const [entetes = [], ...reste] = lignes
  return { entetes, lignes: reste }
}

/** Titre de carte avec son pictogramme dans un carré teinté : la forme dit ce que c'est. */
function TeteCarte({ icone: Icone, titre, texte, droite }: { icone: typeof Bot; titre: string; texte?: string; droite?: ReactNode }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"><Icone className="h-[18px] w-[18px]" /></span>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-900 dark:text-white leading-9">{titre}</p>
        {texte && <p className="text-sm text-slate-500 dark:text-slate-400 -mt-1.5">{texte}</p>}
      </div>
      {droite}
    </div>
  )
}

// ─── Réponse, texto, agents, essai ───────────────────────────────────────────

function ReponseEditor({ form, patch, agents, companyId, tableId }: { form: LookupTableInput; patch: (p: Partial<LookupTableInput>) => void; agents: { id: number; name: string }[]; companyId: number | undefined; tableId: number | null }) {
  const { t } = useTranslation()
  const reelles = form.fields.filter((f) => f.name.trim()).map((f) => ({ id: f.id || slugify(f.name), name: f.name }))
  const adresse = form.fields.find((f) => f.type === 'adresse')
  // Ce que le trajet ajoute sans être une colonne : le temps de route, et la distance
  // ou la ville quand aucune colonne ne les porte déjà.
  const trajet = adresse ? [
    { id: 'distance', name: t('manager.lookup.var.distance', 'Distance en km') },
    { id: 'duree', name: t('manager.lookup.var.duration', 'Temps de route') },
    { id: 'ville', name: t('manager.lookup.var.city', 'Ville') },
  ].filter((x) => !reelles.some((c) => c.id === x.id || c.name === x.name) && !form.fields.some((f) => f.calcul === x.id)) : []
  const colonnes = [...reelles, ...trajet]

  // Phrase de départ quand elle est vide : critères, deux-points, réponses.
  const proposer = () => {
    const c = form.fields.filter((f) => f.role === 'criterion' && f.name.trim()).map((f) => `{${f.id || slugify(f.name)}}`)
    const r = form.fields.filter((f) => f.role === 'answer' && f.name.trim()).map((f) => `{${f.id || slugify(f.name)}}`)
    if (!c.length && !r.length) return
    if (adresse) {
      // L'adresse comprise est redite par le serveur avant la phrase : on ne la répète pas ici.
      const dist = form.fields.find((f) => f.calcul === 'distance')
      patch({ answer_template: t('manager.lookup.proposeTrip', 'À {{distance}} km, environ {{duree}} de route : {{reponses}}.', { distance: `{${dist?.id || 'distance'}}`, duree: '{duree}', reponses: r.join('. ') }) })
      return
    }
    patch({ answer_template: `${c.join(' ')}${c.length && r.length ? ' : ' : ''}${r.join('. ')}.`.trim() })
  }

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-6">
        <div className={cn(cardCls, 'p-5')}>
          <TeteCarte icone={Bot} titre={t('manager.lookup.answerTpl', 'Phrase que l’agent lit')}
            droite={!form.answer_template.trim() && colonnes.length > 0 ? <button type="button" onClick={proposer} className="text-xs font-medium text-blue-600 hover:underline whitespace-nowrap mt-2.5">{t('manager.lookup.propose', 'Proposer une phrase')}</button> : undefined} />
          <ZoneVariables bulle="voix" className="min-h-[88px]" value={versEcran(form.answer_template, colonnes)} onChange={(v) => patch({ answer_template: versServeur(v, colonnes) })} colonnes={colonnes}
            placeholder={t('manager.lookup.answerTplPh', 'Pour @Type de service en @Secteur : @Tarif. Taxes en sus.')} />
          {adresse && <p className="text-xs text-slate-500 mt-2">{t('manager.lookup.answerTripHelp', 'L’agent redit d’abord l’adresse comprise, sauf si la phrase la contient déjà.')}</p>}
        </div>
        <div className={cn(cardCls, 'p-5')}>
          <TeteCarte icone={MessageSquare} titre={t('manager.lookup.smsTpl', 'Texto envoyé à l’appelant')}
            droite={<Switch className="mt-2.5" checked={form.sms_enabled} onCheckedChange={(v) => patch({ sms_enabled: v })} />} />
          {form.sms_enabled && (
            <ZoneVariables bulle="texto" className="min-h-[72px]" value={versEcran(form.sms_template, colonnes)} onChange={(v) => patch({ sms_template: versServeur(v, colonnes) })} colonnes={colonnes}
              placeholder={t('manager.lookup.smsTplPh', 'Vide = même texte que la phrase. Ajoutez votre nom d’entreprise à la fin.')} />
          )}
        </div>
        <div className={cn(cardCls, 'p-5')}>
          <TeteCarte icone={LifeBuoy} titre={t('manager.lookup.fallback', 'Si aucune ligne ne correspond')} />
          <textarea className={cn(inputCls, 'min-h-[64px] rounded-2xl rounded-tl-md')} value={form.fallback_text} onChange={(e) => patch({ fallback_text: e.target.value })} placeholder={t('manager.lookup.fallbackPh', 'Je n’ai pas cette information sous la main. Je prends vos coordonnées et quelqu’un vous rappelle.')} />
        </div>
      </div>
      <div className="space-y-6">
        <div className={cn(cardCls, 'p-5')}>
          <TeteCarte icone={Users} titre={t('manager.lookup.agentsTitle', 'Agents qui consultent cette table')} texte={t('manager.lookup.agentsText', 'La table ne sert qu’aux agents cochés.')} />
          {!agents.length ? <p className="text-sm text-slate-400">{t('manager.lookup.noAgents', 'Aucun agent IA pour l’instant.')}</p> : (
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
              {agents.map((a) => {
                const on = form.agent_ids.includes(a.id)
                return (
                  <label key={a.id} className="inline-flex items-center gap-2.5 py-1.5 text-sm text-slate-800 dark:text-slate-100 cursor-pointer">
                    <input type="checkbox" className="h-4 w-4 accent-blue-500" checked={on}
                      onChange={(e) => patch({ agent_ids: e.target.checked ? [...form.agent_ids, a.id] : form.agent_ids.filter((x) => x !== a.id) })} />
                    {a.name}
                  </label>
                )
              })}
            </div>
          )}
          {agents.length > 0 && !form.agent_ids.length && (
            <p className="mt-3 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 rounded-md px-3 py-2">{t('manager.lookup.noAgentChecked', 'Aucun agent n’utilise cette table pour l’instant : cochez-en un pour qu’elle serve pendant les appels.')}</p>
          )}
        </div>
        {tableId && <EssaiPanel companyId={companyId} tableId={tableId} fields={form.fields} />}
      </div>
    </div>
  )
}

function EssaiPanel({ companyId, tableId, fields }: { companyId: number | undefined; tableId: number; fields: LookupField[] }) {
  const { t } = useTranslation()
  const essai = useTestLookup(companyId, tableId)
  const [crit, setCrit] = useState<Record<string, string>>({})
  const criteres = fields.filter((f) => f.role === 'criterion' && !f.calcul)
  const aAdresse = fields.some((f) => f.type === 'adresse')
  const couleur = useMemo(() => ({ found: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-500/10 dark:text-emerald-300', none: 'text-amber-700 bg-amber-50 dark:bg-amber-500/10 dark:text-amber-300' }), [])
  return (
    <div className={cn(cardCls, 'p-5')}>
      <TeteCarte icone={FlaskConical} titre={t('manager.lookup.tryTitle', 'Essayer sans appeler')} texte={t('manager.lookup.tryText', 'Tapez ce qu’un appelant dirait, même approximatif, et voyez ce que l’agent lirait.')} />
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        {criteres.map((f) => {
          const id = f.id || slugify(f.name)
          return (
            <div key={id} className={cn(f.type === 'adresse' && 'sm:col-span-2')}>
              <label className={labelCls}>{f.name}</label>
              <input className={inputCls} value={crit[id] ?? ''} onChange={(e) => setCrit({ ...crit, [id]: e.target.value })}
                placeholder={f.type === 'adresse' ? t('manager.lookup.tryAddressPh', 'ex. 1909 avenue des Canadiens-de-Montréal, Montréal') : undefined} />
            </div>
          )
        })}
      </div>
      {aAdresse && <p className="text-xs text-slate-500 mb-3">{t('manager.lookup.tryAddressLimit', '20 essais d’adresse par jour.')}</p>}
      <Button variant="outline" size="sm" onClick={() => essai.mutate(crit)} disabled={essai.isPending}>{essai.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('manager.lookup.tryBtn', 'Consulter')}</Button>
      {essai.data && (
        <div className={cn('mt-4 rounded-lg px-4 py-3 text-sm', couleur[essai.data.status as 'found' | 'none'] || 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200')}>
          <p className="text-[11px] uppercase tracking-wider opacity-70 mb-1">{libellesStatut(t)[essai.data.status] || essai.data.status}</p>
          <p>{essai.data.answer || essai.data.message}</p>
          {essai.data.sms_text && <p className="mt-2 text-xs opacity-80 whitespace-pre-line">SMS · {essai.data.sms_text}</p>}
        </div>
      )}
    </div>
  )
}

function libellesStatut(t: (k: string, d: string) => string): Record<string, string> {
  return {
    found: t('manager.lookup.st.found', 'Répondu'), none: t('manager.lookup.st.none', 'Aucune ligne'), missing: t('manager.lookup.st.missing', 'Incomplet'),
    confirm: t('manager.lookup.st.confirm', 'À confirmer'), ambiguous: t('manager.lookup.st.ambiguous', 'Plusieurs lignes'),
  }
}

// ─── Journal ─────────────────────────────────────────────────────────────────

function JournalView({ companyId, tableId, table, onAjouter }: { companyId: number | undefined; tableId: number; table?: LookupTable; onAjouter: (criteres: Record<string, string>) => void }) {
  const { t, i18n } = useTranslation()
  const { data, isLoading } = useLookupQueries(companyId, tableId)
  const [tout, setTout] = useState(false)
  const libelle = libellesStatut(t)
  const noms = Object.fromEntries((table?.fields || []).map((f) => [f.id, f.name]))
  if (isLoading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>

  const depuis = Date.now() - 7 * 24 * 3600 * 1000
  const semaine = (data || []).filter((q) => new Date(q.created_at).getTime() >= depuis)
  const manquantes = (data || []).filter((q) => q.status === 'none')
  const criteresTexte = (q: { criteria: Record<string, unknown> }) => Object.entries(q.criteria).map(([k, v]) => `${noms[k] || k} : ${String(v)}`).join(' · ')
  const versLigne = (q: { criteria: Record<string, unknown> }) => Object.fromEntries(Object.entries(q.criteria).map(([k, v]) => [k, typeof v === 'object' && v ? `${(v as { min?: number }).min ?? ''}-${(v as { max?: number }).max ?? ''}` : String(v ?? '')]))

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className={cn(cardCls, 'px-5 py-4 flex items-center gap-4')}>
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"><Activity className="h-[18px] w-[18px]" /></span>
          <div>
            <p className="text-xl font-semibold text-slate-900 dark:text-white leading-tight">{semaine.length}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.lookup.statWeek', 'demandes cette semaine')}</p>
          </div>
        </div>
        <div className={cn(cardCls, 'px-5 py-4 flex items-center gap-4')}>
          <span className={cn('inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', manquantes.length ? 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300')}><AlertCircle className="h-[18px] w-[18px]" /></span>
          <div>
            <p className="text-xl font-semibold text-slate-900 dark:text-white leading-tight">{manquantes.length}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.lookup.statMissing', 'sans réponse, à ajouter')}</p>
          </div>
        </div>
      </div>

      {manquantes.length > 0 && (
        <div className={cn(cardCls, 'p-5')}>
          <TeteCarte icone={AlertCircle} titre={t('manager.lookup.missingTitle', 'Lignes qu’il vous manque')} texte={t('manager.lookup.missingText', 'Des appelants ont demandé ceci et l’agent n’avait pas la réponse.')} />
          <div className="divide-y divide-slate-100 dark:divide-slate-800 -mx-5">
            {manquantes.map((q) => (
              <div key={q.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <p className="text-sm text-slate-800 dark:text-slate-100">{criteresTexte(q)}</p>
                  <p className="text-xs text-slate-400">{formatDateHeure(q.created_at, i18n.language)}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => onAjouter(versLigne(q))}><Plus className="h-3.5 w-3.5 mr-1.5" />{t('manager.lookup.addThisRow', 'Ajouter cette ligne')}</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={cn(cardCls, 'overflow-hidden')}>
        <button type="button" onClick={() => setTout(!tout)} className="w-full flex items-center gap-3 px-5 py-4 text-left">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"><History className="h-[18px] w-[18px]" /></span>
          <span className="flex-1 font-medium text-slate-900 dark:text-white">{t('manager.lookup.allLog', 'Tout l’historique')} <span className="text-slate-400 font-normal">({data?.length || 0})</span></span>
          <ChevronDown className={cn('h-4 w-4 text-slate-400 transition-transform', tout && 'rotate-180')} />
        </button>
        {tout && (!data?.length ? <p className="px-5 pb-6 text-sm text-slate-400">{t('manager.lookup.logEmpty', 'Aucune consultation pour l’instant.')}</p> : (
          <table className="w-full text-left text-sm border-t border-slate-100 dark:border-slate-800">
            <tbody>
              {data.map((q) => (
                <tr key={q.id} className="border-b last:border-0 border-slate-100 dark:border-slate-800">
                  <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">{formatDateHeure(q.created_at, i18n.language)}</td>
                  <td className="px-5 py-3">
                    <span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', q.status === 'found' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300' : q.status === 'none' ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300')}>{libelle[q.status] || q.status}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-700 dark:text-slate-200">{criteresTexte(q)}</td>
                  <td className="px-5 py-3 text-slate-500 dark:text-slate-400 max-w-md truncate">{q.answer_text}{q.sms_sent && ' · SMS'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    </div>
  )
}
